import React from 'react';
import { ResultComponentProps } from '../../common/types';
import BaseResultDisplay from '../../common/BaseResultDisplay';
import { Table, Button, message, Card, Descriptions, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

// 截断序列显示函数
const truncateSequence = (sequence: string, maxLength: number = 60) => {
  if (!sequence) return '';
  return sequence.length > maxLength ? sequence.substring(0, maxLength) + '...' : sequence;
};

// 摩尔消光系数结果展示组件
// 在单条模式处理中添加对任务管理器数据的支持
const ExtinctionCoefficientResult: React.FC<ResultComponentProps> = ({ data, sequence, title, onDeleteResult, multiResults, onExportAllCSV, ...rest }) => {
  const normalizedResults = React.useMemo(() => {
    if (data && data.results && Array.isArray(data.results)) {
      if (data.results.length === 1 && Array.isArray(data.results[0]?.result?.results)) {
        return data.results[0].result.results;
      }
      return data.results;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [] as any[];
  }, [data]);

  if (normalizedResults.length > 1) {
    const successResults = normalizedResults.filter((item: any) => (item.success !== false) && (item.result || item));

    if (successResults.length === 0) {
      return (
        <BaseResultDisplay
          title={title || '摩尔消光系数分析'}
          data={data}
          onDeleteResult={onDeleteResult}
          multiResults={multiResults}
          showRawData={true}
          showDistribution={false}
        >
          <Card>
            <Descriptions title="无有效数据" bordered column={1}>
              <Descriptions.Item label="提示">没有找到有效的消光系数计算结果。</Descriptions.Item>
            </Descriptions>
          </Card>
        </BaseResultDisplay>
      );
    }

    const handleExportCSV = () => {
      try {
        const headers = ['ID', '序列', '还原状态', '氧化状态', '还原态吸光度(1mg/ml)', '氧化态吸光度(1mg/ml)'].join(',');
        const rows = successResults.map((item: any, index: number) => {
          const result = item.result || item;
          return [
            `"${item.sequence_id || item.id || `序列${index + 1}`}"`,
            `"${item.sequence || result.sequence || ''}"`,
            (result.extinction_coefficient_reduced || 0).toFixed(2),
            (result.extinction_coefficient_oxidized || 0).toFixed(2),
            (result.absorbance_1mg_ml_reduced || 0).toFixed(3),
            (result.absorbance_1mg_ml_oxidized || 0).toFixed(3)
          ].join(',');
        });
        const csvContent = `${headers}\n${rows.join('\n')}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `摩尔消光系数结果_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('CSV导出成功');
      } catch (error) {
        console.error(error);
        message.error('CSV导出失败');
      }
    };

    return (
      <BaseResultDisplay data={data} title={title || `摩尔消光系数分析（${successResults.length}个序列）`} onDeleteResult={onDeleteResult} multiResults showRawData={false} showDistribution={false}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleExportCSV}>导出CSV</Button>
          {onExportAllCSV && <Button icon={<DownloadOutlined />} onClick={onExportAllCSV}>导出所有结果</Button>}
        </div>
        {successResults.map((item: any, index: number) => {
          const result = item.result || item;
          return (
            <div key={index}>
              {index > 0 && <Card bordered={false} style={{ marginTop: 16 }} />}
              <Card bordered={false} style={{ marginBottom: 16 }} title={`序列 ${item.sequence_id || item.id || (index + 1)} - 消光系数分析`}>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="还原态消光系数 (M⁻¹ cm⁻¹)">{(result.extinction_coefficient_reduced || 0).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="氧化态消光系数 (M⁻¹ cm⁻¹)">{(result.extinction_coefficient_oxidized || 0).toFixed(2)}</Descriptions.Item>
                  <Descriptions.Item label="还原态吸光度 (1mg/ml)">{(result.absorbance_1mg_ml_reduced || 0).toFixed(3)}</Descriptions.Item>
                  <Descriptions.Item label="氧化态吸光度 (1mg/ml)">{(result.absorbance_1mg_ml_oxidized || 0).toFixed(3)}</Descriptions.Item>
                  <Descriptions.Item label="序列">{truncateSequence(item.sequence || result.sequence || '')}</Descriptions.Item>
                </Descriptions>
              </Card>
            </div>
          );
        })}
      </BaseResultDisplay>
    );
  }

  // 单序列结果直接渲染（复用多序列的渲染逻辑，避免递归调用自身导致无限循环）
  const singleEntry = normalizedResults.length === 1 ? normalizedResults[0] : null;

  if (singleEntry && (singleEntry.result || singleEntry)) {
    const result = singleEntry.result || singleEntry;
    return (
      <BaseResultDisplay
        data={data}
        title={title || '摩尔消光系数分析'}
        onDeleteResult={onDeleteResult}
        multiResults={multiResults}
        showRawData={false}
        showDistribution={false}
        {...rest}
      >
        <Card bordered={false} style={{ marginBottom: 16 }} title={`序列 ${singleEntry.sequence_id || singleEntry.id || '1'} - 消光系数分析`}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label="还原态消光系数 (M⁻¹ cm⁻¹)">{(result.extinction_coefficient_reduced || 0).toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="氧化态消光系数 (M⁻¹ cm⁻¹)">{(result.extinction_coefficient_oxidized || 0).toFixed(2)}</Descriptions.Item>
            <Descriptions.Item label="还原态吸光度 (1mg/ml)">{(result.absorbance_1mg_ml_reduced || 0).toFixed(3)}</Descriptions.Item>
            <Descriptions.Item label="氧化态吸光度 (1mg/ml)">{(result.absorbance_1mg_ml_oxidized || 0).toFixed(3)}</Descriptions.Item>
            <Descriptions.Item label="序列">{truncateSequence(singleEntry.sequence || result.sequence || '')}</Descriptions.Item>
          </Descriptions>
        </Card>
      </BaseResultDisplay>
    );
  }

  if (Array.isArray(data) && data.length > 0) {
    return <ExtinctionCoefficientResult data={{ results: data }} sequence={sequence} title={title} onDeleteResult={onDeleteResult} multiResults={multiResults} onExportAllCSV={onExportAllCSV} {...rest} />;
  }

  // 单序列模式：表格展示
  let reduced = 0;
  let oxidized = 0;
  let absReduced = 0;
  let absOxidized = 0;

  // 处理任务管理器返回的数据结构
  if (data && data.results && Array.isArray(data.results) && data.results.length > 0) {
    const firstResult = data.results[0];
    if (firstResult.success && firstResult.result) {
      reduced = firstResult.result.extinction_coefficient_reduced || 0;
      oxidized = firstResult.result.extinction_coefficient_oxidized || 0;
      absReduced = firstResult.result.absorbance_1mg_ml_reduced || 0;
      absOxidized = firstResult.result.absorbance_1mg_ml_oxidized || 0;
    }
  } else {
    // 原有的直接数据格式
    reduced = data?.extinction_coefficient_reduced || 0;
    oxidized = data?.extinction_coefficient_oxidized || 0;
    absReduced = data?.absorbance_1mg_ml_reduced || 0;
    absOxidized = data?.absorbance_1mg_ml_oxidized || 0;
  }
  
  return (
    <BaseResultDisplay 
      data={data} 
      title={title || '摩尔消光系数分析'} 
      onDeleteResult={onDeleteResult} 
      multiResults={multiResults}
      showRawData={false}
      showDistribution={false}
      {...rest}
    >
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Descriptions title="消光系数结果" bordered column={1}>
          <Descriptions.Item label="还原态消光系数 (M⁻¹ cm⁻¹)">{reduced.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="氧化态消光系数 (M⁻¹ cm⁻¹)">{oxidized.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="还原态吸光度 (1mg/ml)">{absReduced.toFixed(3)}</Descriptions.Item>
          <Descriptions.Item label="氧化态吸光度 (1mg/ml)">{absOxidized.toFixed(3)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </BaseResultDisplay>
  );
};

export default ExtinctionCoefficientResult;